import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  X, Phone, ArrowRight, Building2, Warehouse, Store, Church,
  Layers, ShieldCheck, Droplets, Wind, Sun, Wrench, Search, FileText,
  Hammer, ClipboardCheck, Check, MapPin, Star, Factory,
} from "lucide-react";
import { COMPANY, IMAGES } from "@/data/content";
import Header from "@/components/site/Header";
import FAQ from "@/components/site/FAQ";
import CTABand from "@/components/site/CTABand";
import Footer from "@/components/site/Footer";
import StickyCallBar from "@/components/site/StickyCallBar";
import QuoteForm from "@/components/site/QuoteForm";

const SITE_URL = "https://honeybeeroofers.com";
const PAGE_PATH = "/commercial-roofing";

const SYSTEMS = [
  { icon: Sun, name: "TPO Roofing", tag: "Most Popular",
    body: "Heat-welded, reflective single-ply membrane that stands up to Puget Sound rain and cuts summer cooling loads. The workhorse for flat and low-slope commercial roofs.",
    best: "Warehouses, retail, office" },
  { icon: Droplets, name: "EPDM Roofing", tag: "Proven Durability",
    body: "Time-tested rubber membrane with exceptional weathering resistance. Flexible through freeze-thaw cycles and forgiving on large, simple roof fields.",
    best: "Industrial, storage, schools" },
  { icon: ShieldCheck, name: "PVC Roofing", tag: "Chemical Resistant",
    body: "Premium single-ply that shrugs off grease, chemicals, and standing water — the go-to for restaurants and buildings with heavy rooftop equipment.",
    best: "Restaurants, retail, medical" },
  { icon: Layers, name: "Modified Bitumen", tag: "Multi-Ply Tough",
    body: "Asphalt-based, multi-layer system with granule or smooth surfacing. Excellent foot-traffic tolerance and easy, localized repairs over its long life.",
    best: "Multi-family, offices, HOAs" },
  { icon: Wind, name: "Standing-Seam Metal", tag: "50+ Year Life",
    body: "Concealed-fastener metal panels that shed Northwest rain and snow for decades with almost zero maintenance. Sharp looks, serious longevity.",
    best: "Churches, retail, ag buildings" },
  { icon: Wrench, name: "Roof Coatings", tag: "Budget Smart",
    body: "Fluid-applied silicone and acrylic restoration systems that seal, reflect, and extend the life of a sound existing roof — often at half the cost of replacement.",
    best: "Re-roof alternatives, large fields" },
];

const PROPERTIES = [
  { icon: Warehouse, num: "01", title: "Warehouses & Industrial",
    body: "Large-span flat and low-slope roofs, skylights, and rooftop units — sealed tight with minimal disruption to your operations." },
  { icon: Store, num: "02", title: "Retail & Office",
    body: "Keep tenants and customers dry and comfortable. We phase work around business hours so you never close a door." },
  { icon: Building2, num: "03", title: "Multi-Family & HOA",
    body: "Apartments, townhomes, and condo associations. Clear communication with boards, managers, and residents from bid to final walkthrough." },
  { icon: Church, num: "04", title: "Churches & Worship",
    body: "Steep-slope sanctuaries and flat fellowship halls alike — respectful crews, careful staging, and honest stewardship of your budget." },
];

const STEPS = [
  { icon: Search, title: "Free On-Site Assessment", body: "We walk your roof, core-check problem areas, and photograph everything — you get a clear written condition report, not a sales pitch." },
  { icon: FileText, title: "Flat-Rate Proposal", body: "One fixed, written number with the full scope spelled out: system, insulation, flashing, warranty, timeline. No allowances that balloon later." },
  { icon: Hammer, title: "Phased Installation", body: "Manufacturer-certified crews install your system in phases planned around your business — daily clean-up, clear tenant notices, safe staging." },
  { icon: ClipboardCheck, title: "Walkthrough & Warranty", body: "Final inspection together, warranty registration, and a maintenance plan option so your investment is protected for the long haul." },
];

const COMMERCIAL_FAQS = [
  { q: "How much does commercial roofing cost in Snohomish County, WA?", a: "Commercial roofing cost depends on the system (TPO, EPDM, PVC, modified bitumen, metal, or coating), roof size, insulation, and tear-off needs. After a free on-site assessment, we give you one fixed, flat-rate written price — no estimates that grow later." },
  { q: "Can you roof our building without disrupting business?", a: "Yes. We phase commercial projects around your operations — off-hours staging, section-by-section installation, and clear communication with tenants, staff, and property managers throughout the job." },
  { q: "What commercial roofing system is best for the Pacific Northwest?", a: "For most flat and low-slope roofs here, TPO and PVC perform exceptionally well against constant rain and ponding water. Modified bitumen suits high-traffic roofs, standing-seam metal is ideal for steep-slope commercial buildings, and roof coatings can extend a sound existing roof at a fraction of replacement cost. We recommend the right system after assessing your building." },
  { q: "Do you offer commercial roof maintenance programs?", a: "Yes — scheduled inspections, drain and gutter clearing, seam and flashing checks, and photo-documented reports. Preventative maintenance is the cheapest way to extend a commercial roof's service life and keep warranties valid." },
  { q: "Which areas do you serve for commercial roofing?", a: "Our commercial division is based in Everett, WA and serves all of Snohomish, King, and Skagit County — including Everett, Marysville, Arlington, Lake Stevens, Snohomish, Monroe, Lynnwood, Mount Vernon, Seattle, and Bellevue." },
];

const MARQUEE_ITEMS = ["TPO", "EPDM", "PVC", "Modified Bitumen", "Standing-Seam Metal", "Roof Coatings"];

function CommercialSeo() {
  useEffect(() => {
    const title = "Commercial Roofing Contractor | Everett & Snohomish County, WA";
    document.title = title;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = "Honey Bee Roofing & Exteriors' commercial division installs TPO, EPDM, PVC, modified bitumen, standing-seam metal & roof coatings for warehouses, retail, office, multi-family & churches across Snohomish, King & Skagit County, WA. Flat-rate pricing, free on-site assessment.";
    setMeta("name", "description", desc);
    setMeta("name", "keywords", "commercial roofing contractor Everett WA, TPO roofing Snohomish County, flat roof replacement Marysville WA, EPDM roofing Arlington WA, commercial roofer King County, metal roofing Skagit County, roof coatings Everett, multi-family roofing WA, warehouse roofing contractor");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", `${SITE_URL}${PAGE_PATH}`);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `${SITE_URL}${PAGE_PATH}`);

    const serviceLd = {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Commercial Roofing",
      name: "Commercial Roofing — Honey Bee Roofing & Exteriors",
      provider: {
        "@type": "RoofingContractor",
        name: COMPANY.name,
        telephone: COMPANY.phone,
        url: SITE_URL,
        address: { "@type": "PostalAddress", streetAddress: "118 SE Everett Mall Way", addressLocality: "Everett", addressRegion: "WA", postalCode: "98208", addressCountry: "US" },
      },
      areaServed: ["Snohomish County WA", "King County WA", "Skagit County WA"],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Commercial Roofing Systems",
        itemListElement: SYSTEMS.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s.name } })),
      },
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: COMMERCIAL_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    };
    let script = document.getElementById("ld-json");
    if (!script) { script = document.createElement("script"); script.id = "ld-json"; script.type = "application/ld+json"; document.head.appendChild(script); }
    script.textContent = JSON.stringify([serviceLd, faqLd]);
  }, []);
  return null;
}

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] } }),
};

const rise = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 0.08 * i, duration: 0.6, ease: "easeOut" } }),
};

function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="commercial-hero" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0 scale-110">
        <img src={IMAGES.roofReplacement} alt="Commercial roofing installation by Honey Bee Roofing & Exteriors in Everett, WA" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="absolute inset-0 bg-black/40" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-honey/40 bg-honey/10 mb-7">
            <Factory className="w-3.5 h-3.5 text-honey" />
            <span className="text-xs font-semibold tracking-wide text-honey uppercase">Introducing the HoneyBee Commercial Division</span>
          </motion.div>

          <h1 className="font-display font-black uppercase text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            {["Big Roofs. ", "Flat Rates. ", "HoneyBee Honest."].map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={lineReveal} initial="hidden" animate="show"
                  className={`block ${i === 1 ? "text-honey" : ""}`}>
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="text-lg text-zinc-300 mt-6 max-w-xl leading-relaxed">
            TPO, EPDM, PVC, modified bitumen, standing-seam metal &amp; roof coatings for warehouses, retail, office,
            multi-family &amp; churches across <span className="text-white font-semibold">Snohomish, King &amp; Skagit County</span>.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6 }}
            className="flex flex-wrap gap-x-6 gap-y-3 mt-7">
            {[
              { icon: ShieldCheck, text: "Licensed & Insured WA" },
              { icon: Search, text: "Free On-Site Assessment" },
              { icon: Star, text: "5.0 Google Rating" },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-2 text-sm text-zinc-200">
                <b.icon className="w-4 h-4 text-honey" /> {b.text}
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.6 }}
            className="mt-8">
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="commercial-call-btn"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold px-6 py-3.5 rounded-sm hover:border-honey hover:text-honey transition-colors">
              <Phone className="w-4 h-4 text-honey" /> Call {COMPANY.phone}
            </a>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }}
          className="bg-carbon/80 backdrop-blur-xl border border-white/10 rounded-md p-6 sm:p-8 shadow-2xl">
          <div className="mb-5">
            <h2 className="text-2xl font-display font-bold text-white">Free Commercial Assessment</h2>
            <p className="text-sm text-zinc-400 mt-1">One fixed, flat-rate number — takes 30 seconds.</p>
          </div>
          <QuoteForm defaultService="Commercial Roofing" />
        </motion.div>
      </motion.div>
    </section>
  );
}

function Marquee() {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div data-testid="commercial-marquee" className="relative border-y border-honey/20 bg-obsidian py-5 overflow-hidden" aria-hidden="true">
      <div className="marquee-track flex whitespace-nowrap w-max">
        {[0, 1].map((half) => (
          <div key={half} className="flex items-center">
            {row.map((item, i) => (
              <span key={`${half}-${i}`} className="flex items-center font-display font-bold uppercase tracking-[0.25em] text-sm text-zinc-500">
                <span className="px-6">{item}</span>
                <span className="text-honey">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CommercialPage() {
  const [modal, setModal] = useState(false);
  const openQuote = () => setModal(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-obsidian min-h-screen">
      <CommercialSeo />
      <Header onQuote={openQuote} />
      <HeroSection />
      <Marquee />

      {/* Systems bento */}
      <section className="relative py-24 honeycomb-bg">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">Commercial Roofing Systems</p>
            <h2 className="text-4xl sm:text-5xl font-display font-black uppercase tracking-tighter text-white">
              The Right System for Your Building
            </h2>
            <p className="text-zinc-400 mt-4 text-lg">
              Every flat and low-slope system we install is manufacturer-certified and spec'd for the Pacific Northwest's
              relentless rain — never a one-size-fits-all bid.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SYSTEMS.map((s, i) => (
              <motion.div key={s.name} custom={i} variants={rise} initial="hidden" whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`system-card-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group relative bg-carbon border border-white/10 rounded-sm p-7 hover:border-honey/50 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-sm bg-honey/15 border border-honey/30 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-honey" />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-honey/80 border border-honey/25 bg-honey/5 px-2 py-1 rounded-sm">
                    {s.tag}
                  </span>
                </div>
                <h3 className="text-xl font-display font-bold text-white">{s.name}</h3>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{s.body}</p>
                <p className="text-xs text-zinc-500 mt-4 pt-4 border-t border-white/5">
                  <span className="text-honey font-semibold">Best for:</span> {s.best}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Property types */}
      <section className="py-24 bg-carbon border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">Who We Serve</p>
            <h2 className="text-4xl sm:text-5xl font-display font-black uppercase tracking-tighter text-white">
              Buildings That Can't Afford a Leak
            </h2>
            <p className="text-zinc-400 mt-4 text-lg leading-relaxed">
              From the Port of Everett industrial corridor to retail strips in Marysville and church sanctuaries in Arlington —
              we protect the buildings our communities work, shop, and gather in.
            </p>
            <div className="relative mt-10 overflow-hidden rounded-sm border border-white/10 group">
              <img src={IMAGES.trust} alt="Commercial roofing crew at work in Snohomish County, WA"
                className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 flex items-center gap-2 text-sm text-zinc-200">
                <MapPin className="w-4 h-4 text-honey" /> Snohomish • King • Skagit County
              </div>
            </div>
          </motion.div>

          <div className="space-y-4">
            {PROPERTIES.map((p, i) => (
              <motion.div key={p.title} custom={i} variants={rise} initial="hidden" whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                data-testid={`property-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex gap-5 bg-obsidian border border-white/10 rounded-sm p-6 hover:border-honey/40 transition-colors duration-300">
                <span className="font-display font-black text-2xl text-honey/30 group-hover:text-honey transition-colors">{p.num}</span>
                <div>
                  <div className="flex items-center gap-2.5">
                    <p.icon className="w-5 h-5 text-honey" />
                    <h3 className="text-lg font-display font-bold text-white">{p.title}</h3>
                  </div>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">How It Works</p>
            <h2 className="text-4xl sm:text-5xl font-display font-black uppercase tracking-tighter text-white">
              Assessment to Warranty, Zero Drama
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} custom={i} variants={rise} initial="hidden" whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                data-testid={`process-step-${i + 1}`}
                className="relative bg-carbon border border-white/10 rounded-sm p-6">
                <span className="absolute -top-3 left-6 bg-honey text-white text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-sm">
                  Step {i + 1}
                </span>
                <s.icon className="w-6 h-6 text-honey mt-3" />
                <h3 className="text-lg font-display font-bold text-white mt-3">{s.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-12 bg-carbon border border-honey/25 rounded-sm p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-1">
              <h3 className="text-xl font-display font-bold text-white">The same HoneyBee promise — at commercial scale</h3>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
                {[
                  "One fixed, flat-rate written price",
                  "Manufacturer-certified installation crews",
                  "Phased work around your business hours",
                  "Photo-documented reports, start to finish",
                  "Preventative maintenance plans available",
                  `Locally owned, licensed & insured (${COMPANY.license})`,
                ].map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-honey mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <button onClick={openQuote} data-testid="commercial-quote-btn"
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-honey text-white font-bold px-7 py-4 rounded-sm hover:bg-honey-600 transition-colors">
              Get My Free Assessment <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      <FAQ items={COMMERCIAL_FAQS} title="Commercial Roofing FAQs" eyebrow="HoneyBee Commercial Division" />
      <CTABand onQuote={openQuote} />
      <Footer />
      <StickyCallBar onQuote={openQuote} />

      {modal && (
        <div data-testid="quote-modal" data-lenis-prevent onClick={() => setModal(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
            className="relative bg-carbon border border-white/10 rounded-md p-6 sm:p-8 w-full max-w-md my-8">
            <button onClick={() => setModal(false)} data-testid="quote-modal-close" className="absolute top-4 right-4 text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-display font-bold text-white mb-1">Free Commercial Assessment</h2>
            <p className="text-sm text-zinc-400 mb-5">One fixed, flat-rate number in 30 seconds.</p>
            <QuoteForm defaultService="Commercial Roofing" onSuccess={() => {}} />
          </motion.div>
        </div>
      )}
    </div>
  );
}
